/**
 * The full-size map's step A (DIRECTION §0.11; 196 (4), the owner: "probably try both"): the 'open' layout — objectives
 * scattered over the region the two bases bound instead of in bands along the line between them; none active, the
 * commanders choose among them. Placed in pairs mirrored through the middle (a share `f` of the way and `side` off the line
 * for one, 1 − f and −side for the other), plus one in the middle when the count is odd, so each side has the same
 * distances to cover; each pair the best of OPEN_LAYOUT.tries seeded candidates by the site score (the worse of the two).
 *
 * The site score is the hook for the planet's sites (§0.11's first pass, lane F's step B: §5.22's score by slope, rise,
 * material and earthwork); today's is the flattest ground. Pure and deterministic: the seed, the anchors, the ground.
 *
 * With `ModeConfig.pointSpread` 'planet' (the owner, Oct 20: "maybe for most simple we could just expand the number of
 * capturable nodes on the whole planet then we could do % node captured, mostly a stand in"): `pointCount` nodes over the
 * whole planet (`placePlanetNodes`), the same pairs mirrored through the plane between the bases.
 */
import type { CapturePoint, GameState, Vec3 } from '../types';
import { OPEN_LAYOUT } from '../constants/action';
import { teamAnchors } from './capturePoints';
import { battlePlanetIndex } from './actionSetup';
import { slopeAt } from './biomes';
import { SeededRandom } from './random';
import { dacos, dcos, dsin } from './dmath';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub, surfaceTangentFrame } from '../math';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { terraceSiteScore } from './siteScore';
import { planetSettlements } from './settlements';

/** A candidate objective site's worth (higher is better) — the hook lane F's site score replaces. */
export type SiteScore = (state: GameState, planetIndex: number, dir: Vec3) => number;

/** Today's site score: the flattest ground over a few people (the slope, negated). */
export const flatSiteScore: SiteScore = (state, planetIndex, dir) => {
  const planet = state.planets[planetIndex];
  return -slopeAt(planet, dir, OPEN_LAYOUT.slopeStepPeople * PERSON_BODY_HEIGHT * personSetOf(state).k);
};

const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b)))));

/**
 * The way from `a` to `b` as a frame: the place a share `f` along it, `side` radians off it. Bases half the circle apart (a ½
 * board) have no one great circle between them — every way round is as short — so the way leaves `a` along its tangent
 * frame's first axis (a fixed choice, the same every time; the open layout fell to one objective there).
 */
function way(a: Vec3, b: Vec3): (f: number, side: number) => Vec3 {
  const sep = arc(a, b), off = vecSub(b, vecScale(a, vecDot(a, b)));
  const dir = vecLength(off) > 1e-9 ? vecNormalize(off) : surfaceTangentFrame(a).tx;
  const n = vecNormalize({ x: a.y * dir.z - a.z * dir.y, y: a.z * dir.x - a.x * dir.z, z: a.x * dir.y - a.y * dir.x });
  return (f, side) => {
    const onLine = vecNormalize(vecAdd(vecScale(a, dcos(sep * f)), vecScale(dir, dsin(sep * f))));
    return side === 0 ? onLine : vecNormalize(vecAdd(vecScale(onLine, dcos(side)), vecScale(n, dsin(side))));
  };
}

/** The 'open' layout's objectives between the battle planet's two team anchors (the board's bases); none without two. */
export function placeOpenObjectives(state: GameState, score: SiteScore = flatSiteScore): CapturePoint[] {
  const bi = battlePlanetIndex(state);
  if (bi < 0) return [];
  // the board's bases where there is one (its two ends exactly), else where the teams stand
  const bases = state.board?.bases.filter(b => b.planetIndex === bi).sort((x, y) => x.team - y.team).map(b => vecNormalize(b.dir));
  const anchors = bases && bases.length >= 2 ? bases : [...teamAnchors(state, bi).values()];
  if (anchors.length < 2) return [];
  const front = { a: anchors[0], b: anchors[1] };
  const sep = arc(front.a, front.b), at = way(front.a, front.b);
  if (sep < 1e-6) return [];
  const count = Math.max(0, state.config.modeConfig.pointCount ?? OPEN_LAYOUT.count);
  if (state.config.modeConfig.pointSpread === 'planet') return placePlanetNodes(state, bi, front.a, front.b, count);
  const rng = new SeededRandom(`${state.config.seed}-open-layout`);
  const placed: Vec3[] = [];
  const clear = (d: Vec3, gap: number) => arc(d, front.a) >= OPEN_LAYOUT.baseClear * sep && arc(d, front.b) >= OPEN_LAYOUT.baseClear * sep
    && placed.every(p => arc(p, d) >= gap);
  // one pick: the best of the tries whose site (and its mirror) clears the bases and the rest; the spacing eased if none does
  const pick = (middle: boolean): void => {
    for (let ease = 1; ease >= 0.25; ease *= 0.5) {
      const gap = OPEN_LAYOUT.spacing * sep * ease;
      let best: Vec3[] | null = null, bestScore = -Infinity;
      for (let k = 0; k < OPEN_LAYOUT.tries; k++) {
        const f = middle ? 0.5 : rng.range(OPEN_LAYOUT.start, 0.5);
        const side = rng.range(-OPEN_LAYOUT.side, OPEN_LAYOUT.side) * sep * (middle ? 0.5 : 1);
        const one = at(f, side);
        const pair = middle ? [one] : [one, at(1 - f, -side)];
        if (!pair.every(d => clear(d, gap)) || (pair.length === 2 && arc(pair[0], pair[1]) < gap)) continue;
        const worth = Math.min(...pair.map(d => score(state, bi, d)));
        if (worth > bestScore) { best = pair; bestScore = worth; }
      }
      if (best) { placed.push(...best); return; }
    }
  };
  if (count % 2 === 1) pick(true);
  while (placed.length + 1 < count) { const before = placed.length; pick(false); if (placed.length === before) break; }
  return placed.map((d, id) => ({ id, planetIndex: bi, surfaceDir: d, owner: null, capturer: null, progress: 0 }));
}

/**
 * The stand-in's nodes over the whole planet: pairs mirrored through the plane between the bases (a node and its mirror
 * each as far from one base as the other is from the other), one on that plane when the count is odd; candidates seeded
 * uniformly over the sphere, the planet's settlements first; each pick the best of OPEN_LAYOUT.tries by lane F's site score
 * (core/siteScore.ts, a `siteHalfPeople` footprint; the worse of the pair), none nearer a base than `baseClear` of the
 * bases' separation, none nearer another than `planetSpacing` of a node's fair share of the sphere (eased if none clears).
 */
function placePlanetNodes(state: GameState, bi: number, a: Vec3, b: Vec3, count: number): CapturePoint[] {
  const sep = arc(a, b);
  const m = vecNormalize(vecSub(a, b));   // the mirror's normal: a ↔ b
  const mirror = (d: Vec3) => vecNormalize(vecSub(d, vecScale(m, 2 * vecDot(d, m))));
  const onPlane = (d: Vec3) => { const p = vecSub(d, vecScale(m, vecDot(d, m))); return vecLength(p) > 1e-9 ? vecNormalize(p) : null; };
  const score = terraceSiteScore({ halfXPeople: OPEN_LAYOUT.siteHalfPeople, halfZPeople: OPEN_LAYOUT.siteHalfPeople });
  const rng = new SeededRandom(`${state.config.seed}-planet-nodes`);
  const anywhere = (): Vec3 => { const z = rng.range(-1, 1), t = rng.range(0, 2 * Math.PI), r = Math.sqrt(Math.max(0, 1 - z * z)); return { x: r * dcos(t), y: r * dsin(t), z }; };
  const settled = planetSettlements(state, bi).map(x => vecNormalize(x.dir));
  const share = Math.sqrt(4 * Math.PI / Math.max(1, count));
  const placed: Vec3[] = [];
  const clear = (d: Vec3, gap: number) => arc(d, a) >= OPEN_LAYOUT.baseClear * sep && arc(d, b) >= OPEN_LAYOUT.baseClear * sep && placed.every(p => arc(p, d) >= gap);
  const pick = (middle: boolean): void => {
    for (let ease = 1; ease >= 0.25; ease *= 0.5) {
      const gap = OPEN_LAYOUT.planetSpacing * share * ease;
      let best: Vec3[] | null = null, bestScore = -Infinity;
      const tryOne = (d: Vec3 | null) => {
        if (!d) return;
        const pair = middle ? [d] : [d, mirror(d)];
        if (!pair.every(x => clear(x, gap)) || (pair.length === 2 && arc(pair[0], pair[1]) < gap)) return;
        const worth = Math.min(...pair.map(x => score(state, bi, x)));
        if (worth > bestScore) { best = pair; bestScore = worth; }
      };
      for (const d of settled) tryOne(middle ? onPlane(d) : d);
      for (let k = 0; k < OPEN_LAYOUT.tries; k++) { const d = anywhere(); tryOne(middle ? onPlane(d) : d); }
      if (best) { placed.push(...(best as Vec3[])); return; }
    }
  };
  if (count % 2 === 1) pick(true);
  while (placed.length + 1 < count) { const before = placed.length; pick(false); if (placed.length === before) break; }
  return placed.map((d, id) => ({ id, planetIndex: bi, surfaceDir: d, owner: null, capturer: null, progress: 0 }));
}
